import React from 'react'
import PropTypes from 'prop-types'
import { BillboardText, Card, colors, IconButton, Paragraph, Row, Separator } from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

/**
 * @typedef {'person' | 'event' | 'personAndEvent'} NoPrimaryAlertVariant
 */

/**
 * Builds the message, tooltip description and tooltip label for a given variant.
 *
 * @param {Object} params
 * @param {NoPrimaryAlertVariant} params.variant - Which primary element(s) the household is missing
 * @param {Function} params.t - i18next translate function
 * @returns {{message: string, tooltipLabel: string, tooltipDescription: string}}
 */
const getAlertCopy = ({ variant, t }) => {
  if (variant === 'event') {
    return {
      message: t('no-primary-alert.event.message', 'This group has no primary event'),
      tooltipLabel: t('no-primary-alert.event.label', 'How to add a primary event for this group'),
      tooltipDescription: t('no-primary-alert.event.tooltip', 'Edit a group member’s events to add a primary event.'),
    }
  }

  if (variant === 'personAndEvent') {
    return {
      message: t('no-primary-alert.person-and-event.message', 'This group has no primary person or primary event'),
      tooltipLabel: t(
        'no-primary-alert.person-and-event.label',
        'How to set a primary person and primary event for this group'
      ),
      tooltipDescription: t(
        'no-primary-alert.person-and-event.tooltip',
        'Edit the essential information of a group member to set a primary person. Edit a household member’s events to add a primary event.'
      ),
    }
  }

  return {
    message: t('no-primary-alert.person.message', 'This group has no primary person'),
    tooltipLabel: t('no-primary-alert.person.label', 'How to set a primary person for this group'),
    tooltipDescription: t(
      'no-primary-alert.person.tooltip',
      'Edit the essential information of a group member to set a primary person.'
    ),
  }
}

/**
 * Derives the alert variant for a household from what it is missing.
 *
 * @param {Object} params
 * @param {boolean} params.hasPrimaryPerson - Whether the household has a primary person
 * @param {boolean} params.hasPrimaryEvent - Whether the household has a primary event
 * @returns {?NoPrimaryAlertVariant} The variant to render, or null when nothing is missing
 */
export const getNoPrimaryVariant = ({ hasPrimaryPerson, hasPrimaryEvent }) => {
  if (!hasPrimaryPerson && !hasPrimaryEvent) return 'personAndEvent'
  if (!hasPrimaryPerson) return 'person'
  if (!hasPrimaryEvent) return 'event'
  return null
}

/**
 * Inline alert shown when a household is missing a primary person, a primary event, or both.
 *
 * Pass a falsy variant for the cleared state rather than unmounting: the live region has to
 * stay mounted for the clear to be announced.
 *
 * @component
 * @param {Object} props
 * @param {?NoPrimaryAlertVariant} [props.variant] - Which primary element(s) the household is missing
 * @param {string} [props.dataTestid] - Optional test id applied to the alert card
 * @param {'both'|'below'|'none'} [props.gutters='both'] - Which of the alert's own vertical gutters
 *   to render. 'below' is for callers with a divider already above it; 'none' for callers whose
 *   layout supplies its own spacing.
 * @returns {JSX.Element}
 */
const NoPrimaryAlert = ({ variant, dataTestid, gutters = 'both' }) => {
  const { t } = useTranslation()

  if (!variant) return null

  const { message, tooltipLabel, tooltipDescription } = getAlertCopy({ variant, t })
  const testid = dataTestid || `no-primary-${variant}-alert`

  return (
    <>
      {gutters === 'both' && <Separator size="xxs" />}
      <Card margins="xxs" data-testid={testid} role="status" aria-live="polite">
        {/* Paragraph has no color prop; BillboardText sets the --special-type-color that Zion
            typography reads. Set here so EventsCard's disabled-state wrapper cannot gray it out. */}
        <BillboardText as="div" primaryColor={colors.yellow60}>
          <Row alignY="middle" guttersY="pico">
            <Paragraph size="sm" growX>
              {message}
            </Paragraph>
            {/* IconButton rather than a bare icon so keyboard users can reach the instructions. */}
            <IconButton
              Icon={NoticeInfo}
              aria-label={tooltipLabel}
              description={tooltipDescription}
              color="yellow"
              size="xs"
              data-testid={`${testid}-info`}
            />
          </Row>
        </BillboardText>
      </Card>
      {gutters !== 'none' && <Separator size="xxs" />}
    </>
  )
}

NoPrimaryAlert.propTypes = {
  variant: PropTypes.oneOf(['person', 'event', 'personAndEvent', null]),
  dataTestid: PropTypes.string,
  gutters: PropTypes.oneOf(['both', 'below', 'none']),
}

export default NoPrimaryAlert
